import { fetchStores } from '@/api/store';
import { deleteVoucher, bulkDeleteVouchers, fetchVoucherss } from '@/api/voucher';
import { listRates, deleteRate, Rate } from '@/api/rate';
import PageTitle from '@/components/Admin/PageTitle';
import Table from '@/components/Admin/Table';
import { useUIStore } from '@/store/useUIStore';
import { DropdownActionType } from '@/types/shared';
import { useQuery } from '@tanstack/react-query';

import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { TableStore } from './Stores';
import { colors } from '@/constants/colors';
import { currencyFormat } from '@/utils/currencyFormatter';
import IconsLib from '@/utils/IconsLib';
import { idChunks } from '@/utils/idChunks';

export type TableVouchers = {
   _id: string;
   code: string;
   store: string;
   storeId: string;
   price: number;
   sold: boolean;
   createdAt: string;
};

export type PageTitleActions = {
   type: 'normal' | 'button';
   title: string;
   onClick?: () => void;
};

const STORAGE_KEY = 'smartfi-voucher-filters';
const PER_PAGE = 15;

type SortOption = 'newest' | 'oldest' | 'name-asc' | 'name-desc' | 'rate-asc' | 'rate-desc';

type VoucherFilters = {
   status: 'all' | 'available' | 'sold';
   store: string;
   rate: string;
   sort: SortOption;
};

const loadFilters = (): VoucherFilters => {
   try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) return JSON.parse(saved);
   } catch {}
   return { status: 'all', store: '', rate: '', sort: 'newest' };
};

const sortVouchers = (data: TableVouchers[], sort: SortOption): TableVouchers[] => {
   const sorted = [...data];
   switch (sort) {
      case 'newest':
         return sorted.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      case 'oldest':
         return sorted.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
      case 'name-asc':
         return sorted.sort((a, b) => a.code.localeCompare(b.code));
      case 'name-desc':
         return sorted.sort((a, b) => b.code.localeCompare(a.code));
      case 'rate-asc':
         return sorted.sort((a, b) => a.price - b.price);
      case 'rate-desc':
         return sorted.sort((a, b) => b.price - a.price);
      default:
         return sorted;
   }
};

const Vouchers = () => {
   const { refresh, handleClose, handleRefresh, handleShow } = useUIStore();
   const [filters, setFilters] = useState<VoucherFilters>(loadFilters);
   const [page, setPage] = useState(1);
   const [ratesStoreFilter, setRatesStoreFilter] = useState<string>('');
   const [selected, setSelected] = useState<Set<string>>(new Set());
   const [bulkDeleting, setBulkDeleting] = useState(false);
   const [deletingAll, setDeletingAll] = useState(false);

   const { data: response, isLoading, error } = useQuery({
      queryKey: ['voucher', refresh],
      queryFn: fetchVoucherss,
   });

   const { data: storeResponse } = useQuery({
      queryKey: ['stores', refresh],
      queryFn: fetchStores,
   });

   const { data: rates = [] } = useQuery({
      queryKey: ['rates', refresh, ratesStoreFilter],
      queryFn: () => listRates(ratesStoreFilter || undefined),
   });

   useEffect(() => {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
   }, [filters]);

   useEffect(() => {
      setPage(1);
   }, [filters]);

   const updateFilter = (key: keyof VoucherFilters, value: string) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
   };

   const transformedData: TableVouchers[] =
      response?.vouchers?.map((v: any) => ({
         _id: v._id,
         code: v.code,
         store: v.store,
         storeId: v.storeId,
         price: v.price,
         sold: v.sold,
         createdAt: v.createdAt,
      })) || [];

   const transformedStoreData =
      storeResponse?.result?.map((store: TableStore) => ({
         ...store,
         _id: store._id,
         name: store.storename,
      })) || [];

   const storeOptions = useMemo(() => {
      const stores = [...new Set(transformedData.map((v) => v.store))];
      return stores.sort();
   }, [transformedData]);

   const rateOptions = useMemo(() => {
      const r = [...new Set(transformedData.map((v) => v.price))];
      return r.sort((a, b) => a - b);
   }, [transformedData]);

   const filteredData = useMemo(() => {
      const filtered = transformedData.filter((v) => {
         if (filters.status === 'available' && v.sold) return false;
         if (filters.status === 'sold' && !v.sold) return false;
         if (filters.store && v.store !== filters.store) return false;
         if (filters.rate && v.price !== Number(filters.rate)) return false;
         return true;
      });
      return sortVouchers(filtered, filters.sort);
   }, [transformedData, filters]);

   const totalPages = Math.ceil(filteredData.length / PER_PAGE);
   const paginatedData = filteredData.slice(
      (page - 1) * PER_PAGE,
      page * PER_PAGE
   );

   const hasActiveFilters =
      filters.status !== 'all' ||
      filters.store !== '' ||
      filters.rate !== '' ||
      filters.sort !== 'newest';

   const clearFilters = () => {
      setFilters({ status: 'all', store: '', rate: '', sort: 'newest' });
   };

   const toggleSelect = (id: string) => {
      setSelected((prev) => {
         const next = new Set(prev);
         if (next.has(id)) next.delete(id);
         else next.add(id);
         return next;
      });
   };

   const toggleSelectAll = () => {
      if (selected.size === paginatedData.length) {
         setSelected(new Set());
      } else {
         setSelected(new Set(paginatedData.map((v) => v._id)));
      }
   };

   const handleBulkDelete = () => {
      const count = selected.size;
      handleShow({
         template: 'verification',
         title: `Bulk Delete — ${count} voucher${count !== 1 ? 's' : ''}`,
         windowType: 'desktop',
         data: {
            data: null,
            msg: `Are you sure you want to delete ${count} voucher${count !== 1 ? 's' : ''}? This action cannot be undone.`,
         },
         actions: {
            onConfirm: async () => {
               try {
                  setBulkDeleting(true);
                  const res = await bulkDeleteVouchers([...selected]);
                  toast.success(res.msg, { autoClose: 2500 });
                  setSelected(new Set());
                  handleClose();
                  handleRefresh();
               } catch {
                  toast.error('Failed to delete vouchers');
               } finally {
                  setBulkDeleting(false);
               }
            },
            onCancel: () => {},
         },
      });
   };

   const handleDeleteAll = () => {
      const ids = filteredData.map((v) => v._id);
      const count = ids.length;
      if (!count) return;
      handleShow({
         template: 'verification',
         title: `Delete All — ${count} voucher${count !== 1 ? 's' : ''}`,
         windowType: 'desktop',
         data: {
            data: null,
            msg: hasActiveFilters
               ? `This will permanently delete all ${count} vouchers currently listed (your filters are applied — vouchers outside the filter are kept). This cannot be undone.`
               : `This will permanently delete ALL ${count} of your vouchers. This cannot be undone.`,
            confirmWord: 'DELETE',
         },
         actions: {
            onConfirm: async () => {
               try {
                  setDeletingAll(true);
                  for (const chunk of idChunks(ids)) {
                     await bulkDeleteVouchers(chunk);
                  }
                  toast.success(`Deleted ${count} voucher${count !== 1 ? 's' : ''}`, {
                     autoClose: 2500,
                  });
                  setSelected(new Set());
                  setPage(1);
                  handleClose();
                  handleRefresh();
               } catch {
                  toast.error('Failed to delete vouchers — some may already be deleted. Refresh and try again.');
                  handleRefresh();
               } finally {
                  setDeletingAll(false);
               }
            },
            onCancel: () => {},
         },
      });
   };

   // Clear selection when page or filters change
   useEffect(() => {
      setSelected(new Set());
   }, [page, filters]);

   const pageTitleActions: PageTitleActions[] = [
      {
         type: 'normal',
         title: 'Import',
         onClick: () => {
            handleShow({
               template: 'import-voucher',
               title: `Import Vouchers`,
               windowType: 'desktop',
               data: transformedStoreData,
            });
         },
      },
      {
         type: 'button',
         title: 'Create Voucher',
         onClick: () => {
            handleShow({
               template: 'create-voucher',
               title: 'Create Voucher',
               windowType: 'desktop',
               data: transformedStoreData,
            });
         },
      },
   ];

   const dropdownActions: DropdownActionType<TableVouchers>[] = [
      {
         label: 'Edit',
         onClick: (data: TableVouchers) => {
            handleShow({
               template: 'edit-voucher',
               title: `Edit Voucher - ${data.code}`,
               windowType: 'desktop',
               data: data,
            });
         },
         icon: 'edit-2-line',
      },
      {
         label: 'Delete',
         onClick: (data: TableVouchers) => {
            handleShow({
               template: 'verification',
               title: `Delete Confirmation - ${data.code}`,
               windowType: 'desktop',
               data: {
                  data: data,
                  msg: 'Are you sure you want to delete this voucher? This action cannot be undone.',
               },
               actions: {
                  onConfirm: async (voucher: unknown) => {
                     try {
                        const v = voucher as TableVouchers;
                        const res = await deleteVoucher(v._id);
                        toast.success(res.msg, { autoClose: 2500 });
                        handleClose();
                        handleRefresh();
                     } catch {
                        toast.error('Something went wrong');
                     }
                  },
                  onCancel: () => {},
               },
            });
         },
         icon: 'delete-bin-6-line',
      },
   ];

   const openEditRate = (rate: Rate) => {
      handleShow({
         template: 'edit-rate',
         title: `Edit rate — ${currencyFormat(rate.price, true)}`,
         windowType: 'desktop',
         data: rate,
      });
   };

   const handleDeleteRate = (rate: Rate) => {
      handleShow({
         template: 'verification',
         title: `Delete Rate — ${currencyFormat(rate.price, true)}`,
         windowType: 'desktop',
         data: {
            data: rate,
            msg: `Delete the ${currencyFormat(rate.price, true)} rate from ${rate.storeName}? This only removes the rate entry — no vouchers will be affected.`,
         },
         actions: {
            onConfirm: async (target: unknown) => {
               try {
                  const r = target as Rate;
                  const res = await deleteRate(r._id);
                  toast.success(res.msg, { autoClose: 2500 });
                  handleClose();
                  handleRefresh();
               } catch (err: any) {
                  toast.error(err?.response?.data?.err || 'Failed to delete rate');
               }
            },
            onCancel: () => {},
         },
      });
   };

   const storeIdByName = useMemo(() => {
      const map = new Map<string, string>();
      transformedStoreData.forEach((s: any) => map.set(s.storename, s._id));
      return map;
   }, [transformedStoreData]);

   return (
      <Wrapper>
         <PageTitle title="Vouchers" actions={pageTitleActions} />

         <FilterBar>
            <FilterGroup>
               <FilterLabel>Status</FilterLabel>
               <FilterSelect
                  value={filters.status}
                  onChange={(e) => updateFilter('status', e.target.value)}
               >
                  <option value="all">All</option>
                  <option value="available">Available</option>
                  <option value="sold">Sold</option>
               </FilterSelect>
            </FilterGroup>

            <FilterGroup>
               <FilterLabel>Store</FilterLabel>
               <FilterSelect
                  value={filters.store}
                  onChange={(e) => updateFilter('store', e.target.value)}
               >
                  <option value="">All Stores</option>
                  {storeOptions.map((store) => (
                     <option key={store} value={store}>
                        {store}
                     </option>
                  ))}
               </FilterSelect>
            </FilterGroup>

            <FilterGroup>
               <FilterLabel>Rate</FilterLabel>
               <FilterSelect
                  value={filters.rate}
                  onChange={(e) => updateFilter('rate', e.target.value)}
               >
                  <option value="">All</option>
                  {rateOptions.map((r) => (
                     <option key={r} value={r}>
                        ₱{r}
                     </option>
                  ))}
               </FilterSelect>
            </FilterGroup>

            <FilterGroup>
               <FilterLabel>Sort</FilterLabel>
               <FilterSelect
                  value={filters.sort}
                  onChange={(e) => updateFilter('sort', e.target.value)}
               >
                  <option value="newest">Newest first</option>
                  <option value="oldest">Oldest first</option>
                  <option value="name-asc">Name A-Z</option>
                  <option value="name-desc">Name Z-A</option>
                  <option value="rate-asc">Rate low-high</option>
                  <option value="rate-desc">Rate high-low</option>
               </FilterSelect>
            </FilterGroup>

            {hasActiveFilters && (
               <ClearBtn onClick={clearFilters}>Clear filters</ClearBtn>
            )}

            <ResultCount>
               {filteredData.length} voucher{filteredData.length !== 1 ? 's' : ''}
            </ResultCount>

            {filteredData.length > 0 && (
               <DeleteAllBtn onClick={handleDeleteAll} disabled={deletingAll}>
                  {deletingAll
                     ? 'Deleting...'
                     : `Delete all (${filteredData.length})`}
               </DeleteAllBtn>
            )}
         </FilterBar>

         <Columns>
            <MainCol>
               {isLoading ? (
                  <LoadingText>Loading vouchers...</LoadingText>
               ) : error ? (
                  <ErrorText>Error loading vouchers: {error.message}</ErrorText>
               ) : (
                  <>
                     {selected.size > 0 && (
                        <BulkBar>
                           <BulkInfo>
                              {selected.size} selected
                           </BulkInfo>
                           <BulkDeleteBtn onClick={handleBulkDelete} disabled={bulkDeleting}>
                              {bulkDeleting ? 'Deleting...' : 'Delete selected'}
                           </BulkDeleteBtn>
                           <BulkCancelBtn onClick={() => setSelected(new Set())}>
                              Cancel
                           </BulkCancelBtn>
                        </BulkBar>
                     )}
                     <Table<TableVouchers>
                        headData={[
                           { type: 'checkbox', value: '' },
                           { type: 'title', value: 'Voucher Code' },
                           { type: 'title', value: 'Store' },
                           { type: 'title', value: 'Rate' },
                           { type: 'title', value: 'Date Added' },
                           { type: 'title', value: 'Status' },
                           { type: 'title', value: 'Action' },
                        ]}
                        data={paginatedData}
                        grid={`36px 140px 1fr 100px 130px 100px auto`}
                        type="voucher"
                        dropdownData={dropdownActions}
                        checkboxSelection={{
                           selected,
                           onToggle: toggleSelect,
                           onToggleAll: toggleSelectAll,
                        }}
                     />

                     {totalPages > 1 && (
                        <Pagination>
                           <PageBtn
                              disabled={page === 1}
                              onClick={() => setPage((p) => Math.max(1, p - 1))}
                           >
                              Prev
                           </PageBtn>
                           <PageNumbers>
                              {Array.from({ length: totalPages }, (_, i) => i + 1)
                                 .filter((p) => {
                                    if (totalPages <= 7) return true;
                                    if (p === 1 || p === totalPages) return true;
                                    if (Math.abs(p - page) <= 1) return true;
                                    return false;
                                 })
                                 .reduce<(number | string)[]>((acc, p, idx, arr) => {
                                    if (idx > 0 && p - (arr[idx - 1] as number) > 1) {
                                       acc.push('...');
                                    }
                                    acc.push(p);
                                    return acc;
                                 }, [])
                                 .map((p, idx) =>
                                    typeof p === 'string' ? (
                                       <Ellipsis key={`e-${idx}`}>...</Ellipsis>
                                    ) : (
                                       <PageNum
                                          key={p}
                                          $active={p === page}
                                          onClick={() => setPage(p)}
                                       >
                                          {p}
                                       </PageNum>
                                    )
                                 )}
                           </PageNumbers>
                           <PageBtn
                              disabled={page === totalPages}
                              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                           >
                              Next
                           </PageBtn>
                        </Pagination>
                     )}
                  </>
               )}
            </MainCol>

            <SideCol>
               <SideCard>
                  <SideHeader>
                     <SideTitle>Rates</SideTitle>
                     <StoreFilter
                        value={ratesStoreFilter}
                        onChange={(e) => setRatesStoreFilter(e.target.value)}
                     >
                        <option value="">All stores</option>
                        {transformedStoreData.map((s: any) => (
                           <option key={s._id} value={s._id}>
                              {s.storename}
                           </option>
                        ))}
                     </StoreFilter>
                  </SideHeader>

                  {rates.length === 0 ? (
                     <SideEmpty>
                        No rates yet. They appear automatically once you add a
                        voucher at a new price.
                     </SideEmpty>
                  ) : (
                     <RateList>
                        {rates.map((r) => (
                           <RateItem key={r._id}>
                              <RateTop>
                                 <RatePrice>
                                    {currencyFormat(r.price, true)}
                                 </RatePrice>
                                 <RateStoreTag>{r.storeName}</RateStoreTag>
                                 <RateEdit
                                    onClick={() => openEditRate(r)}
                                    aria-label="Edit rate"
                                 >
                                    <IconsLib
                                       icon="edit-2-line"
                                       size="15px"
                                       color={colors.PRIMARY}
                                    />
                                 </RateEdit>
                              </RateTop>
                              <RateTitle $empty={!r.title}>
                                 {r.title || 'Untitled rate — tap edit'}
                              </RateTitle>
                              {r.description ? (
                                 <RateDesc
                                    dangerouslySetInnerHTML={{
                                       __html: r.description,
                                    }}
                                 />
                              ) : (
                                 <RateDescMuted>
                                    Add a description so customers know what
                                    they're buying.
                                 </RateDescMuted>
                              )}
                              <RateFoot>
                                 <RateStock $empty={r.availableCount === 0}>
                                    {r.availableCount} available
                                 </RateStock>
                                 {r.availableCount === 0 && (
                                    <RateDeleteBtn onClick={() => handleDeleteRate(r)}>
                                       Delete
                                    </RateDeleteBtn>
                                 )}
                              </RateFoot>
                           </RateItem>
                        ))}
                     </RateList>
                  )}
               </SideCard>
            </SideCol>
         </Columns>
      </Wrapper>
   );
};

const Wrapper = styled.div``;

const FilterBar = styled.div`
   display: flex;
   align-items: flex-end;
   gap: 12px;
   padding: 12px 1rem;
   flex-wrap: wrap;
   border-bottom: 1px solid ${colors.GRAY13};
   @media (max-width: 768px) {
      padding: 10px 12px;
      gap: 8px;
   }
`;

const FilterGroup = styled.div`
   display: flex;
   flex-direction: column;
   gap: 4px;
   @media (max-width: 768px) {
      flex: 1 1 calc(50% - 4px);
      min-width: 0;
   }
`;

const FilterLabel = styled.label`
   font-size: 12px;
   color: ${colors.GRAY4};
   font-weight: 500;
`;

const FilterSelect = styled.select`
   outline: none;
   border: 1px solid ${colors.GRAY12};
   border-radius: 4px;
   padding: 6px 10px;
   font-size: 13px;
   color: ${colors.GRAY1};
   background: var(--app-surface);
   min-width: 120px;
   cursor: pointer;

   &:focus {
      border-color: ${colors.PRIMARY};
   }

   @media (max-width: 768px) {
      min-width: 0;
      width: 100%;
   }
`;

const ClearBtn = styled.button`
   background: none;
   border: none;
   color: ${colors.PRIMARY};
   font-size: 13px;
   cursor: pointer;
   padding: 6px 0;
   align-self: flex-end;

   &:hover { text-decoration: underline; }
`;

const ResultCount = styled.span`
   font-size: 13px;
   color: ${colors.GRAY5};
   margin-left: auto;
   align-self: flex-end;
   padding-bottom: 6px;
`;

const Columns = styled.div`
   display: grid;
   grid-template-columns: 1fr 320px;
   gap: 16px;
   padding: 0 1rem;
   @media (max-width: 1100px) {
      grid-template-columns: 1fr;
      padding: 0 12px;
   }
`;

const MainCol = styled.div``;
const SideCol = styled.aside`
   padding-top: 12px;
`;
const SideCard = styled.div`
   position: sticky;
   top: 16px;
   border: 1px solid ${colors.GRAY13};
   border-radius: 12px;
   background: var(--app-surface);
   overflow: hidden;
`;
const SideHeader = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 8px;
   padding: 12px 14px;
   border-bottom: 1px solid ${colors.GRAY13};
`;
const SideTitle = styled.h3`
   font-size: 14px;
   font-weight: 700;
   color: ${colors.GRAY_DARK};
`;
const StoreFilter = styled.select`
   outline: none;
   border: 1px solid ${colors.GRAY12};
   border-radius: 6px;
   padding: 5px 8px;
   font-size: 12px;
   color: ${colors.GRAY1};
   background: var(--app-surface);
   cursor: pointer;
   max-width: 160px;
`;
const SideEmpty = styled.p`
   font-size: 12.5px;
   color: ${colors.GRAY4};
   padding: 16px 14px;
   line-height: 1.5;
`;
const RateList = styled.div`
   display: flex;
   flex-direction: column;
   max-height: 70vh;
   overflow-y: auto;
`;
const RateItem = styled.div`
   padding: 12px 14px;
   border-bottom: 1px solid ${colors.GRAY13};
   display: flex;
   flex-direction: column;
   gap: 4px;
   &:last-child { border-bottom: none; }
`;
const RateTop = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
`;
const RatePrice = styled.span`
   font-size: 15px;
   font-weight: 800;
   color: ${colors.PRIMARY};
`;
const RateStoreTag = styled.span`
   font-size: 10.5px;
   text-transform: uppercase;
   letter-spacing: 0.06em;
   color: ${colors.GRAY5};
   padding: 2px 8px;
   border-radius: 999px;
   background: var(--app-surface-muted);
   white-space: nowrap;
   overflow: hidden;
   text-overflow: ellipsis;
   max-width: 140px;
`;
const RateEdit = styled.button`
   margin-left: auto;
   width: 26px;
   height: 26px;
   border-radius: 6px;
   border: 1px solid ${colors.GRAY12};
   background: var(--app-surface);
   display: grid;
   place-items: center;
   cursor: pointer;
   &:hover { border-color: ${colors.PRIMARY}; }
`;
const RateTitle = styled.p<{ $empty: boolean }>`
   font-size: 13px;
   font-weight: ${({ $empty }) => ($empty ? 500 : 700)};
   color: ${({ $empty }) => ($empty ? colors.GRAY4 : colors.GRAY_DARK)};
   font-style: ${({ $empty }) => ($empty ? 'italic' : 'normal')};
   line-height: 1.4;
`;
const RateDesc = styled.div`
   font-size: 12px;
   color: ${colors.GRAY3};
   line-height: 1.55;
   p { margin: 0; }
`;
const RateDescMuted = styled.p`
   font-size: 11.5px;
   color: ${colors.GRAY5};
   font-style: italic;
`;
const RateFoot = styled.div`
   margin-top: 2px;
   display: flex;
   align-items: center;
   gap: 6px;
`;
const RateDeleteBtn = styled.button`
   margin-left: auto;
   background: none;
   border: 1px solid ${colors.DANGER};
   color: ${colors.DANGER};
   border-radius: 4px;
   padding: 2px 8px;
   font-size: 11px;
   font-weight: 600;
   cursor: pointer;
   &:hover { background: ${colors.DANGER}; color: white; }
`;

const RateStock = styled.span<{ $empty?: boolean }>`
   font-size: 11px;
   color: ${({ $empty }) => ($empty ? colors.DANGER : colors.SUCCESS)};
   background: ${({ $empty }) => ($empty ? 'rgba(220, 53, 69, 0.1)' : 'rgba(25, 135, 84, 0.1)')};
   padding: 2px 8px;
   border-radius: 999px;
   font-weight: 600;
`;

const Pagination = styled.div`
   display: flex;
   align-items: center;
   justify-content: center;
   gap: 8px;
   padding: 16px 1rem;
`;
const PageBtn = styled.button<{ disabled?: boolean }>`
   display: flex;
   align-items: center;
   gap: 4px;
   background: none;
   border: 1px solid ${({ disabled }) => (disabled ? colors.GRAY12 : colors.GRAY10)};
   border-radius: 4px;
   padding: 6px 12px;
   font-size: 13px;
   color: ${({ disabled }) => (disabled ? colors.GRAY10 : colors.GRAY1)};
   cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
   &:hover:not(:disabled) { background: var(--app-surface-muted); }
`;
const PageNumbers = styled.div`
   display: flex;
   align-items: center;
   gap: 4px;
`;
const PageNum = styled.button<{ $active: boolean }>`
   background: ${({ $active }) => ($active ? colors.PRIMARY : 'none')};
   color: ${({ $active }) => ($active ? 'white' : colors.GRAY1)};
   border: 1px solid ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY12)};
   border-radius: 4px;
   width: 32px;
   height: 32px;
   font-size: 13px;
   cursor: pointer;
   &:hover {
      background: ${({ $active }) => ($active ? colors.PRIMARY : 'var(--app-surface-muted)')};
   }
`;
const Ellipsis = styled.span`
   color: ${colors.GRAY5};
   font-size: 13px;
   padding: 0 4px;
`;
const BulkBar = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
   padding: 10px 1rem;
   background: ${colors.GRAY14};
   border-bottom: 1px solid ${colors.GRAY13};
   @media (max-width: 768px) {
      padding: 8px 12px;
   }
`;

const BulkInfo = styled.span`
   font-size: 13px;
   font-weight: 600;
   color: ${colors.GRAY1};
`;

const BulkDeleteBtn = styled.button`
   background: ${colors.DANGER};
   color: white;
   border: none;
   border-radius: 6px;
   padding: 6px 14px;
   font-size: 12px;
   font-weight: 600;
   cursor: pointer;
   &:hover { opacity: 0.9; }
   &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const DeleteAllBtn = styled.button`
   background: none;
   color: ${colors.DANGER};
   border: 1px solid ${colors.DANGER};
   border-radius: 6px;
   padding: 6px 14px;
   font-size: 12px;
   font-weight: 600;
   cursor: pointer;
   &:hover { background: ${colors.DANGER}; color: white; }
   &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const BulkCancelBtn = styled.button`
   background: none;
   border: none;
   color: ${colors.GRAY4};
   font-size: 12px;
   cursor: pointer;
   &:hover { color: ${colors.GRAY1}; }
`;

const LoadingText = styled.div`
   text-align: center;
   padding: 2rem;
   color: ${colors.GRAY5};
`;
const ErrorText = styled.div`
   text-align: center;
   padding: 2rem;
   color: ${colors.DANGER};
`;

export default Vouchers;
